import { css } from "@emotion/css";
import { ArrowRightIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { KubernetesLiveStatusDetailedResource, TimelineItem } from "@octopusdeploy/octopus-server-client";
import React from "react";
import { LiveStatusResourceIcon } from "~/areas/projects/components/Observability/LiveStatusIcons";
import { TimeLineItemCard } from "~/areas/tasks/components/Task/Kubernetes/components/DiffDeploymentSelector";
import DateFormatter from "~/utils/DateFormatter/index";
type ManifestDiffHeaderProps = {
    latestDeployment: TimelineItem;
    liveStatusResource?: Pick<KubernetesLiveStatusDetailedResource, "HealthStatus" | "LastUpdated">;
};
const styles = {
    container: css({
        display: "flex",
        flexDirection: "row",
        gap: space[24],
        padding: space[8],
        borderRadius: borderRadius["medium"],
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        backgroundColor: themeTokens.color.background.secondary.default,
    }),
    label: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.primary,
        paddingBottom: space[4],
    }),
    itemContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[8],
    }),
    itemTextContainer: css({
        display: "flex",
        flexDirection: "column",
    }),
    itemTitle: css({
        font: text.body.bold.large,
        color: themeTokens.color.text.primary,
    }),
    itemSubtitle: css({
        font: text.body.regular.xSmall,
        lineHeight: "1rem", //NOTE: this is not available in the theme-tokens (xs=1.25).
        color: themeTokens.color.text.secondary,
    }),
    gridContainer: css({
        display: "grid",
        gridTemplateColumns: ["1fr min-content 1fr"],
        gridTemplateRows: ["min-content min-content"],
    }),
    alignArrowCentrally: css({
        display: "flex",
        alignItems: "center",
        paddingLeft: space[24],
        paddingRight: space[24],
    }),
};
export const ManifestDiffHeader = (props: ManifestDiffHeaderProps) => (<div className={styles.gridContainer}>
        <span className={styles.label}>Compare the applied manifest</span>
        <div />
        <span className={styles.label}>with the live manifest on the cluster</span>
        <div className={styles.container}>
            <TimeLineItemCard timelineItem={props.latestDeployment}/>
        </div>
        <div className={styles.alignArrowCentrally}>
            <ArrowRightIcon size={24}/>
        </div>
        <div className={styles.container}>
            <LiveItemCard liveStatusResource={props.liveStatusResource}/>
        </div>
    </div>);
function LiveItemCard(props: {
    liveStatusResource?: Pick<KubernetesLiveStatusDetailedResource, "HealthStatus" | "LastUpdated">;
}) {
    return (<div className={styles.itemContainer}>
            <LiveStatusResourceIcon status={props.liveStatusResource?.HealthStatus || "Unknown"}></LiveStatusResourceIcon>
            <div className={styles.itemTextContainer}>
                <div className={styles.itemTitle}>Live</div>
                <div className={styles.itemSubtitle}>{props.liveStatusResource?.LastUpdated ? `Last updated ${DateFormatter.momentAgo(props.liveStatusResource?.LastUpdated)}` : "Last updated an unknown amount of time ago"}</div>
            </div>
        </div>);
}
